import { memo, useEffect, useState } from 'react';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded';

export interface TaskCheckboxProps {
  checked: boolean;
  interactive?: boolean;
  /** Accessible name, normally the task text. */
  label?: string;
}

/**
 * Checkbox for a GFM task list item.
 *
 * When interactive, the checked state is UI-only: the Markdown source is never
 * mutated, so the document remains the single source of truth.
 */
export const TaskCheckbox = memo(function TaskCheckbox({
  checked,
  interactive = true,
  label,
}: TaskCheckboxProps) {
  const [value, setValue] = useState(checked);

  useEffect(() => setValue(checked), [checked]);

  return (
    <Checkbox
      checked={value}
      disabled={!interactive}
      disableRipple
      size="small"
      onChange={(event) => setValue(event.target.checked)}
      inputProps={{ 'aria-label': label ?? 'Task item' }}
      icon={<CheckBoxOutlineBlankRoundedIcon fontSize="small" />}
      checkedIcon={<CheckBoxRoundedIcon fontSize="small" />}
      sx={{
        p: 0,
        mr: 0.75,
        mt: '0.28em',
        alignSelf: 'flex-start',
        color: 'text.disabled',
        '&.Mui-checked': { color: 'primary.main' },
        '&.Mui-disabled': { color: 'text.disabled' },
      }}
    />
  );
});

export default TaskCheckbox;
